import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Award, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

export default function PracticalCredits() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", activity_name: "", cfu: 1, date: "", activity_type: "uscita missionaria", note: "" });

  const { data: credits = [] } = useQuery({
    queryKey: ["practical-credits-all"],
    queryFn: async () => (await api.get("/schedule/practical-credits")).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-pc"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo" } })).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/schedule/practical-credits", { ...form, cfu: parseFloat(form.cfu) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["practical-credits-all"] }); toast.success("Credito assegnato"); setOpen(false); setForm({ student_id: "", activity_name: "", cfu: 1, date: "", activity_type: "uscita missionaria", note: "" }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/schedule/practical-credits/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["practical-credits-all"] }); toast.success("Rimosso"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const totalCfu = credits.reduce((s, c) => s + (c.cfu || 0), 0);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">
      <PageHeader
        title="Crediti di Teologia Pratica"
        subtitle="Assegna CFU pratici per uscite missionarie, servizio pastorale e attività sul campo."
        actions={
          <Button data-testid="pc-add-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Plus size={16} className="mr-1.5"/> Assegna credito
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Award size={11}/> Crediti assegnati</div>
          <div className="text-2xl font-semibold mt-1">{credits.length}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">CFU pratici totali</div>
          <div className="text-2xl font-semibold text-[#E2725B] mt-1">{totalCfu}</div>
        </Card>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        {credits.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">Nessun credito assegnato ancora.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {credits.map((c) => (
              <li key={c.id} data-testid={`pc-row-${c.id}`} className="p-4 flex items-center gap-4 hover:bg-stone-50">
                <div className="w-10 h-10 rounded-full bg-[#E2725B] text-white grid place-items-center shrink-0"><Award size={14}/></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{c.activity_name}</div>
                    <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">{c.cfu} CFU</Badge>
                    {c.activity_type && <Badge className="bg-stone-100 text-stone-700 text-[10px] rounded-full">{c.activity_type}</Badge>}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    👤 {c.student_name} · {c.matricola} · {fmtDate(c.date)} · assegnato da {c.assigned_by_name || "—"}
                  </div>
                  {c.note && <div className="text-sm text-stone-600 mt-1">{c.note}</div>}
                </div>
                <button onClick={() => delMut.mutate(c.id)} className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700" data-testid={`pc-del-${c.id}`}>
                  <Trash2 size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Assegna credito pratico</DialogTitle>
            <DialogDescription>Il credito comparirà nel libretto dello studente sotto la sezione &laquo;Teologia Pratica&raquo;.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (!form.student_id || !form.activity_name) return; createMut.mutate(); }} className="space-y-3">
            <div>
              <Label className="text-xs">Studente</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="pc-student"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.matricola}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nome attività</Label>
              <Input required data-testid="pc-activity" value={form.activity_name} onChange={(e) => setForm({ ...form, activity_name: e.target.value })} placeholder="Es. Uscita missionaria di Aprile"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">CFU</Label>
                <Input type="number" step="0.5" min="0.5" required data-testid="pc-cfu" value={form.cfu} onChange={(e) => setForm({ ...form, cfu: e.target.value })}/>
              </div>
              <div>
                <Label className="text-xs">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/>
              </div>
            </div>
            <div>
              <Label className="text-xs">Tipo attività</Label>
              <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="uscita missionaria">Uscita missionaria</SelectItem>
                  <SelectItem value="viaggio missionario">Viaggio missionario</SelectItem>
                  <SelectItem value="servizio pastorale">Servizio pastorale</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={createMut.isPending} data-testid="pc-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
                {createMut.isPending ? "Salvataggio…" : "Assegna"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
